import { useMemo, useRef, useCallback, LegacyRef } from 'react'
import { HotTable } from '@handsontable/react'
import Handsontable from 'handsontable'
import { ListEditor } from '@admin/template-elements/edit-list/list-editor'
import { TextRendererWithDataAttributes } from '@admin/template-elements/edit-list/list-table-cell-renderer'
import { LICENSE_KEY } from '@global/constants/handsontable'
import { useListCache } from '@global/hooks/queries/useListCache'
import { ListDTOResponse, IListColumn } from '@global/types'
import { appendRowColHeaderAttributes } from '@utils/append-row-col-header-attributes'
import { toDataRowsFromListColumns } from 'functions'
import { t } from '@lingui/core/macro'

/**
 * Rename the existing column from the context menu.
 */
function renameExistingColumn() {
  //
}

/**
 * Rename the existing column from the context menu.
 */
function renameNewColumn() {
  //
}

/**
 * Rename the existing column from the context menu.
 */
function afterRemoveRows() {
  //
}

/**
 * Rename the existing column from the context menu.
 */
function afterAddRow() {
  //
}

/**
 * Rename the existing column from the context menu.
 */
function moveToStart() {
  //
}

/**
 * TODO: Look into if after change is necessary - seems to be redundant.
 */
function afterChange() {
  //
}

function addColumnHeaderDataAttributes(idx, th) {
  appendRowColHeaderAttributes(th, idx, null)
}

function addRowHeaderDataAttributes(idx, th) {
  appendRowColHeaderAttributes(th, null, idx)
}

type ListResponseToTableData = Omit<ListDTOResponse, 'list_columns'>

export interface IListTableData extends ListResponseToTableData {
  list_columns: Partial<IListColumn>[]
  columnsByName: string[]
  values: Handsontable.RowObject[]
}

interface SimpleCellCoords {
  row: number
  col: number
}

interface ITableConfigReturn {
  settings: Handsontable.GridSettings
  setTableRef: LegacyRef<HotTable> | undefined
  setContainerRef: (node: HTMLDivElement) => void
  isReadOnlyActive: boolean
}

export const useTableConfiguration = ({
  list,
}: {
  list: IListTableData | undefined
}): ITableConfigReturn => {
  const tableRef = useRef<HotTable | null>(null)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const readOnly = false
  const isDropdown = list?.type === 'drop-down'
  const isPoweredByTemplate = Boolean(list?.powered_template_id)
  const isReadOnlyActive = isPoweredByTemplate || readOnly
  const { afterRemoveCol } = useListCache({ listId: list?.id })

  // Is it necessary to check templatesById for the list.powered_template_id,
  // can we check just if list.powered_template_id is null
  // const isPoweredByTemplate = useMemo(
  //   () => isEmpty(templatesById[list.powered_template_id]),
  //   [list.powered_template_id, templatesById],
  // )

  const setTableRef = useCallback((node: HotTable) => {
    tableRef.current = node
  }, [])

  const setContainerRef = useCallback((node: HTMLDivElement) => {
    tableContainerRef.current = node
  }, [])

  const contextMenu = useMemo(
    () => ({
      items: {
        rename_column: {
          name: t`Rename Column`,
          callback: renameExistingColumn,
          hidden: () => Boolean(list?.powered_template_id),
        },
        '---------': {
          hidden: () => isDropdown || Boolean(list?.powered_template_id),
        },
        remove_col: {
          name: t`Remove Column`,
          hidden: () => isDropdown || Boolean(list?.powered_template_id),
        },
        col_left: {
          name: t`Insert Column Before`,
          callback: renameNewColumn,
          hidden: () => isDropdown || Boolean(list?.powered_template_id),
        },
        remove_row: {
          name: t`Remove Row`,
          hidden: () => Boolean(list?.powered_template_id),
          callback: afterRemoveRows,
        },
        row_above: {
          name: t`Insert Row`,
          hidden: () => Boolean(list?.powered_template_id),
          callback: afterAddRow,
        },
        move_column_to_start: {
          name: t`Move Column To Start`,
          callback: moveToStart,
          hidden: () => isDropdown,
        },
      },
    }),
    [isDropdown, list?.powered_template_id],
  )

  const beforePaste = useCallback((data, _cords) => {
    const table = tableRef?.current?.hotInstance
    const selection = table?.getSelected()
    const singleSetSelection = selection?.length === 1

    // We are currently only supporting a single selection (not skipped rows in the selection).
    if (!singleSetSelection || !selection[0]) return

    const [startRow, _startCol, endRow, _endCol] = selection[0]
    const isMultipleRowsSelected = endRow > 0 && startRow !== endRow
    const positiveNumSelection = Math.abs(endRow - startRow)
    const numberOfRowsSelected = positiveNumSelection + 1 // 1 = include starting row

    if (isMultipleRowsSelected) {
      data.splice(numberOfRowsSelected)
    }
  }, [])

  const tabMoves = useCallback((event: KeyboardEvent): SimpleCellCoords => {
    const table = tableRef?.current?.hotInstance
    const selected = table?.getSelected()

    if (!selected) return { row: 0, col: 0 }

    const countCols = table?.countCols() || 1 // default to 1 if undefined
    const selectedStart = selected[0]
    const lastColumnPosition = countCols - 1

    // if we are on the end and tabbing
    if (!event.shiftKey && selectedStart && selectedStart[1] === lastColumnPosition) {
      return { row: 1, col: -lastColumnPosition }
    }

    // if we are on the first and going backwards
    if (event.shiftKey && selectedStart && selectedStart[1] === 0) {
      return { row: 1, col: -lastColumnPosition }
    }

    return { row: 0, col: 1 }
  }, [])

  const data = useMemo(
    () =>
      list?.list_columns && list?.list_columns?.length > 0
        ? toDataRowsFromListColumns(list.list_columns, false)
        : [],
    [list?.list_columns],
  )

  const settings: Handsontable.GridSettings = {
    data,
    height: tableContainerRef.current?.offsetHeight,
    licenseKey: LICENSE_KEY,
    colWidths: 250,
    contextMenu,
    editor: ListEditor,
    fillHandle: true,
    manualColumnFreeze: true,
    manualColumnMove: isPoweredByTemplate,
    manualColumnResize: true,
    maxCols: isDropdown ? 1 : Infinity,
    minSpareRows: 10,
    readOnly: readOnly || isPoweredByTemplate,
    rowHeaders: true,
    rowHeights: 40,
    startCols: 1,
    copyPaste: {
      columnsLimit: 100,
      rowsLimit: 10000,
    },
    afterChange, // TEMP
    afterGetColHeader: addColumnHeaderDataAttributes,
    afterGetRowHeader: addRowHeaderDataAttributes,
    afterRemoveCol,
    beforePaste,
    colHeaders: index => list?.columnsByName[index] ?? t`Column Name`,
    renderer: TextRendererWithDataAttributes,
    tabMoves,
  }

  return {
    settings,
    setTableRef,
    setContainerRef,
    isReadOnlyActive,
  }
}
